import {inject, injectable} from "inversify";
import * as LocalStorage from "localstorage";
import "reflect-metadata";
import HttpService from "./http.service";
import ToastService from "./toast.service";
import { AuthenticateService, OpenAPI, UserService } from "./openapi/generated";
import { UserInformation } from "../typings";

enum UserType {
    ARTIST = "artist",
    COLLECTOR = "collector",
    ADMIN = "admin",
}

@injectable()
export default class IdentityService {
    public get isArtist() {
        if (this.userInfo) {
            return this.userInfo.type === UserType.ARTIST;
        }

        return false;
    }

    public get isCollector() {
        if (this.userInfo) {
            return this.userInfo.type === UserType.COLLECTOR;
        }

        return false;
    }

    public get isVerified() {
        if (this.userInfo) {
            return this.userInfo.is_verified;
        }

        return false;
    }

    public get walletAddress() {
        if (this.userInfo) {
            return this.userInfo.wallet_address;
        }

        return "";
    }

    public get isAdmin() {
        if (this.userInfo) {
            return this.userInfo.type === UserType.ADMIN;
        }

        return false;
    }
    public static storage: any;
    public interval: any;

    @inject(HttpService)
    public httpService: HttpService;

    @inject(ToastService)
    public toastService: ToastService;

    public userInfo?: UserInformation;

    constructor() {
        IdentityService.storage = new LocalStorage("encryptoart:");
    }

    public isAuthenticated = () => {
        return this.getToken() != null;
    };

    public async authenticate() {
        if (this.getToken() != null) {
            await this.refreshToken(true);
        }
    }

    public activate(uid: string, token: string): Promise<any> {
        return new Promise<any>((resolve, reject) => {
            this.httpService
                .post("activate/", { uid, token })
                .then((result) => {
                    resolve(true);
                })
                .catch((error) => {
                    reject(error);
                });
        });
    }

    public async refreshToken(refreshUserInfo?: boolean) {
        await this.refreshUserInfo();
        // .then(() => {

        // })
        // .catch((error) => {
        //     IdentityService.storage.put("token", null);
        //     (window as any).store.dispatch({type: "CHANGE_AUTH_STATUS", value: false});
        //     (window as any).store.dispatch({type: "CHANGE_USER_INFO", value: undefined});

        //     this.disableRefreshToken();
        // });
        if (this.interval == null) {
            this.enableRefreshInterval();
        }
        // IdentityService.storage.put("token", result.token);
    }

    public logout = () => {
        IdentityService.storage.del("token");
        (window as any).store.dispatch({ type: "CHANGE_AUTH_STATUS", value: false });
        (window as any).store.dispatch({ type: "CHANGE_USER_INFO", value: undefined });
        this.disableRefreshToken();
    };

    public getToken() {
        const [_e, value] = IdentityService.storage.get("token");
        const [_f, expiry] = IdentityService.storage.get("expiry");
        return expiry > new Date().toISOString() ? value : null;
    }

    public async refreshUserInfo() {
        (window as any).store.dispatch({ type: "CHANGE_AUTH_STATUS", value: true });
        OpenAPI.TOKEN = this.getToken();
        const response = await this.getUserInformationWithHeaders();
        IdentityService.storage.put("expiry", new Date(Date.now() + 60 * 60000).toISOString());
        const user = await response;
        // @ts-ignore
        this.userInfo = user as UserInformation;
        (window as any).store.dispatch({ type: "CHANGE_USER_INFO", value: user });
    }

    public login(email: string, password: string): Promise<any> {
        return AuthenticateService.authControllerLogin({ email, password})
            .then((response) => {
                IdentityService.storage.put("token", response.access_token);
                OpenAPI.TOKEN = response.access_token;
                IdentityService.storage.put("expiry", new Date(Date.now() + 60 * 60000).toISOString());

                // TODO: 2FA
                // response.json().then ...

                (window as any).store.dispatch({ type: "OPEN_LOGIN_MODAL", inPage: 0, value: false });

                this.refreshUserInfo();

                this.enableRefreshInterval();
            }).catch((error) => {
                this.toastService.showToast("Error", error.body.message, "error");
            });
    }

    public update(data: Partial<UserInformation>, refresh?: boolean): Promise<any> {
        return UserService.userControllerUpdate(data).then((result) => { this.refreshToken(refresh); }).catch((error) => {
            this.toastService.showToast("Error", error.body.message, "error");
        });
    }

    public forgotPassword(email): Promise<any> {
        return new Promise<any>((resolve, reject) => {
            UserService.userControllerRequestResetPassword(email).then((result) => {
                resolve(result);
            })
                .catch((error) => {
                    reject(error);
                });
        });
    }

    public resetPassword(uid, token, newPassword): Promise<any> {
        return new Promise<any>((resolve, reject) => {
            UserService.userControllerResetPassword(token, { password: newPassword }).then((result) => {
                    resolve(result);
                })
                .catch((error) => {
                    reject(error);
                });
        });
    }

    public signUp(email: string, password: string, name: string, userRole: number): Promise<any> {
        return UserService.userControllerCreate({
            email,
            password,
            type: userRole.toString(),
            name,
        }).then((result) => {
            (window as any).store.dispatch({ type: "OPEN_LOGIN_MODAL", inPage: 0, value: false });
        }).catch((error) => {
            this.toastService.showToast("Error", error.body.message, "error");
        });
    }

    public getUserInformation() {
        return UserService.userControllerInfo().catch((error) => {
            this.toastService.showToast("Error", error.body.message, "error");
        });
    }

    public getUserInformationWithHeaders() {
        return UserService.userControllerInfo().catch((error) => {
            this.toastService.showToast("Error", error.body.message, "error");
        });
    }

    private enableRefreshInterval() {
        this.interval = setInterval(this.refreshToken.bind(this), 14 * 60 * 1000); // refresh token every 14 mins
    }

    private disableRefreshToken() {
        clearInterval(this.interval);
    }
}



// WEBPACK FOOTER //
// ./src/services/identity.service.ts